import React, { useState } from 'react';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText,
  Button,
  HeaderText,
  Stack,
} from '../../../ui';

import ProgressBar from '../../today/SearchBoost/ProgressBar';
import { RadialGradientCameraIcon, ChevronIcon } from '../../../ui/Icons';
import useMount from '../../../../hooks/useMount';
import analytics from '../../../../modules/analytics';
import { updateIncentiveCollapseCount } from '../index';

type IncentiveToAddProps = {
  /** The number of unique clients that have been sent the invite to book sms */
  clientsInvited: number;
  /** If the first render will show the collapsed version */
  initialCollapsed: boolean;
  /** If we are viewing in mobile layout */
  inMobileLayout: boolean;
  /** If profile is p1c complete */
  profileComplete: boolean;
};

export const IncentiveToAdd: React.FC<IncentiveToAddProps> = ({
  clientsInvited,
  initialCollapsed,
  inMobileLayout,
  profileComplete,
}) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);

  /**
   * handles collapsing the two different views
   */
  const handleCollapseClick = () => {
    if (!collapsed) {
      updateIncentiveCollapseCount();
      analytics.track('pro_clients_instagrambanner_collapse');
    } else {
      analytics.track('pro_clients_instagrambanner_expand');
    }
    setCollapsed(!collapsed);
  };

  useMount(() => {
    analytics.track('pro_clients_instagrambanner_view');
  });

  return (
    <Box
      paddingX={20}
      paddingY={collapsed ? 16 : 20}
      color={colors.itsOff}
      style={{
        width: !inMobileLayout ? 600 : null,
      }}
    >
      {collapsed && (
        <Box>
          <Inline
            alignItems="center"
            justifyContent="space-between"
            maxHeight={28}
          >
            <Inline
              space={8}
              alignItems="center"
            >
              <RadialGradientCameraIcon
                width={16}
                height={16}
              />
              <BodyText
                appearance="small"
              >
                Get featured on the StyleSeat Instagram
              </BodyText>
            </Inline>
            <Button
              appearance="none"
              onClick={handleCollapseClick}
              testID="incentive-to-add-expand-button"
            >
              <ChevronIcon
                direction="down"
                color={colors.ash}
              />
            </Button>
          </Inline>
          <Box
            paddingLeft={24}
          >
            <BodyText
              appearance="smaller"
              color={colors.metal}
            >
              {`${profileComplete ? clientsInvited : 0}/5 Clients Invited`}
            </BodyText>
          </Box>
        </Box>
      )}
      {!collapsed && (
        <Stack
          space={8}
        >
          <Inline
            justifyContent="space-between"
            alignItems="baseline"
          >
            <HeaderText
              level={2}
            >
              Invite 5 Clients and enter for a chance to be featured on the StyleSeat Instagram!
            </HeaderText>
            <Button
              appearance="none"
              onClick={handleCollapseClick}
              testID="incentive-to-add-collapse-button"
            >
              <ChevronIcon
                direction="up"
                color={colors.ash}
              />
            </Button>
          </Inline>
          <BodyText
            appearance="medium"
            color={colors.metal}
          >
            Winners are promoted to our 35k followers
            {'\n'}
            <Button
              appearance="none"
              href="https://www.styleseat.com/blog/styleseats-invite-to-book-instagram-promotion-sweepstakes-official-rules/"
              linkTarget="_blank"
              size="small"
              style={{
                marginRight: 4,
              }}
              testID="incentive-to-add-link-to-terms-and-conditions"
            >
              <BodyText
                appearance="medium-link-lowercase"
                color={colors.metal}
                textTransform="none"
              >
                Terms and Conditions
              </BodyText>
            </Button>
            apply
          </BodyText>
          <ProgressBar
            currentValue={clientsInvited}
            maxValue={5}
            paddingTop={8}
            progressColor={colors.punch}
            progressPosition="flex-start"
            icon={profileComplete ? 'radialCameraIcon' : 'disabledCamera'}
            backgroundColor={colors.whiteWash}
            disabled={!profileComplete}
            hideCount
          />
          <BodyText
            appearance="smaller"
            color={colors.metal}
          >
            {profileComplete ? `${clientsInvited}/5 Clients Invited` : 'Complete your profile to unlock'}
          </BodyText>
        </Stack>
      )}
    </Box>
  );
};
